import { useCallback, useMemo } from "react";
import React from "react";

import { Rename } from "components/Prompts";
import { useDealParamsHooks, useIsViewerPermission, usePrompt } from "hooks";
import { useDealRoleGuard, useModals } from "hooks";
import { useDealActions, useViewActions } from "store";
import { IFormMetaDetail } from "store/types";
import { IItemToShare } from "store/types/deal/share";

import { EntityType, RoleName } from "../../../constants";
import { ContextMenuOptionObjectType } from "../../popups/Context-menu/Options-collection";

export interface UseFormHookParams {
  form: IFormMetaDetail;
  refetchHandler?: () => Promise<void>;
}

export const useFormHook = ({ form, refetchHandler }: UseFormHookParams) => {
  const { dealId } = useDealParamsHooks();
  const openPrompt = usePrompt();
  const { deleteModal, errorModal } = useModals();
  const { setShareDialog } = useViewActions();
  const { updateFormAction, deleteFormAction, duplicateFormAction, patchFolderMetaDetailAction } = useDealActions();
  const isAnalystDealsViewerPermission = useIsViewerPermission("deals");
  const isAvailableRoleWithPermissions = isAnalystDealsViewerPermission
    ? [RoleName.Admin]
    : [RoleName.Admin, RoleName.Analyst];

  const handleShare = useCallback(() => {
    setShareDialog({
      elements: [form.id],
      type: "form_id",
      shareName: form.title,
      dealId: dealId ?? null,
      itemToShare: form.is_private ? null : (form as unknown as IItemToShare),
      cb: async () => {
        await refetchHandler?.();
      }
    });
  }, [setShareDialog, form, dealId]);

  const handleDelete = useCallback(() => {
    deleteModal({
      element: "Delete item permanently?",
      message: "This cannot be undone",
      onPressSuccess: "Delete permanently",
      action: async () => {
        await deleteFormAction({ dealId, formId: form.id });
        patchFolderMetaDetailAction({
          key: "delete",
          id: form.id,
          entityType: EntityType.FORM
        });
      }
    });
  }, [deleteModal, deleteFormAction, form, dealId, patchFolderMetaDetailAction]);

  const handleRename = useCallback(
    async (menuTriggerRef: React.RefObject<HTMLButtonElement>, onMenuClose: () => void) => {
      if (!form.id) return;
      const trigger = menuTriggerRef.current;
      if (!trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const anchorPosition = {
        top: Math.round(triggerRect.bottom),
        left: Math.round(triggerRect.left)
      };

      onMenuClose();

      const title = await openPrompt({
        Component: Rename,
        componentType: "popover",
        anchorPosition,
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        props: { title: form.title }
      });
      if (title) {
        await updateFormAction({ formId: form.id, dealId, title });
        patchFolderMetaDetailAction({
          key: "patch",
          id: form.id,
          entityType: EntityType.FORM,
          patch: { title }
        });
      }
    },
    [form.id, form.title, dealId, openPrompt, updateFormAction, patchFolderMetaDetailAction]
  );

  const handleDuplicate: (() => Promise<void>) | ContextMenuOptionObjectType = useMemo(
    () =>
      form.kind === "static"
        ? async () => {
            await duplicateFormAction({ formId: form.id });
            await refetchHandler?.();
          }
        : {
            action: async (value: string) => {
              try {
                await duplicateFormAction({
                  formId: form.id,
                  recurringDate: value
                });
                await refetchHandler?.();
              } catch (error) {
                errorModal((error as any)?.errors?.recurring_date);
                throw error;
              }
            },
            withDatePicker: true
          },
    [form.kind, form.id, duplicateFormAction, refetchHandler, errorModal]
  );

  const handleConvertToPeriodic: ContextMenuOptionObjectType | undefined = useMemo(() => {
    if (form.kind === "static") {
      return {
        action: async (value: string) => {
          try {
            await updateFormAction({
              formId: form.id,
              dealId,
              kind: "recurring",
              recurring_date: value
            });
            patchFolderMetaDetailAction({
              key: "patch",
              id: form.id,
              entityType: EntityType.FORM,
              patch: { kind: "recurring" }
            });
          } catch (error) {
            errorModal((error as any)?.errors?.recurring_date);
            throw error;
          }
        },
        withDatePicker: true
      };
    }
  }, [form.kind, form.id, dealId, updateFormAction, patchFolderMetaDetailAction, errorModal]);

  const handleShareWithRole = useDealRoleGuard(handleShare, isAvailableRoleWithPermissions);
  const handleDeleteWithRole = useDealRoleGuard(handleDelete, isAvailableRoleWithPermissions);
  const handleRenameWithRole = useDealRoleGuard(handleRename, isAvailableRoleWithPermissions);
  const handleDuplicateWithRole = useDealRoleGuard(handleDuplicate, isAvailableRoleWithPermissions);
  const handleCancelPeriodic: (() => Promise<void>) | undefined = useMemo(() => {
    if (form.kind === "recurring") {
      return async () => {
        await updateFormAction({ formId: form.id, dealId, kind: "static" });
        patchFolderMetaDetailAction({
          key: "patch",
          id: form.id,
          entityType: EntityType.FORM,
          patch: { kind: "static" }
        });
      };
    }
  }, [form.kind, form.id, dealId, updateFormAction, patchFolderMetaDetailAction]);

  const handleConvertToPeriodicWithRole = useDealRoleGuard(handleConvertToPeriodic, isAvailableRoleWithPermissions);
  const handleCancelPeriodicWithRole = useDealRoleGuard(handleCancelPeriodic, isAvailableRoleWithPermissions);

  return {
    handleShare: handleShareWithRole,
    handleDelete: handleDeleteWithRole,
    handleRename: handleRenameWithRole,
    handleDuplicate: handleDuplicateWithRole,
    handleConvertToPeriodic: handleConvertToPeriodicWithRole,
    handleCancelPeriodic: handleCancelPeriodicWithRole
  };
};
